import React from 'react';
import { noop } from 'lodash';
import { Reply as ReplyIcon } from '@wix/wix-ui-icons-common/on-stage';
import { REPLY_BTN, REPLY_TEXT } from '../../../../common/constants/data-hooks';
import { getIsMobile } from '../../../../common/store/base-params/base-params-selectors';
import { useTranslate } from '../../../context/api-context/translations/use-translate';
import { useApi } from '../../../context/api-context/use-api';
import { useConfiguration } from '../../../context/configuration-context/use-configuration';
import { useReactionConfig } from '../../../context/configuration-context/reaction-config/use-reaction-config';
import { unreachable } from '../../../../ts-utils';
import { generateLocalCommentId } from '../../comment-list/id-utils';
import { LikeButton, EmotionsButton } from '../../../../reactions';
import Voting from '../../voting/voting';
import LikesMetadataLazy from '../../reactions/likes-metadata-lazy';
import EmotionsMetadataLazy from '../../reactions/emotions-metadata-lazy';
import Skeleton from '../../skeleton/skeleton';
import { classes as classesReactions } from '../comment-reactions.st.css';
import { st, classes } from './comment-footer.st.css';
export const CommentFooter = ({ replyButtonState, commentId, canReact, canVote, reactionsState, votesState, interactionFetchState, }) => {
    const { resourceId, ctxFields, likeButtonText, replyButtonText } = useConfiguration();
    const reactionsConfig = useReactionConfig();
    const t = useTranslate();
    const { reactToComment, voteOnComment, initReply, isMobile } = useApi((state, actions) => ({
        reactToComment: actions.reactToComment,
        voteOnComment: actions.voteOnComment,
        initReply: actions.initReply,
        isMobile: getIsMobile(state),
    }));
    const replyButton = (() => {
        switch (replyButtonState) {
            case 'hidden':
                return null;
            case 'disabled':
                return React.createElement(ReplyButton, { disabled: true });
            case 'enabled':
                return (React.createElement(ReplyButton, { onReply: () => {
                        const localId = generateLocalCommentId();
                        initReply({
                            resourceId,
                            targetId: commentId,
                            localId,
                        });
                    }, replyButtonText: replyButtonText }));
            default:
                throw unreachable(replyButtonState);
        }
    })();
    switch (reactionsConfig.type) {
        case 'none':
            return React.createElement(CommentFooterLayout, { aside: null }, replyButton);
        case 'likes': {
            return (React.createElement(CommentFooterLayout, { aside: React.createElement(LikesMetadataLazy, { reactionsState: reactionsState, commentId: commentId, config: reactionsConfig, t: t, isPending: reactionsState.pending }) },
                React.createElement(Skeleton, { showSkeleton: interactionFetchState === 'not_fetched' },
                    React.createElement("div", { className: classes.contentWrapper },
                        React.createElement(LikeButton, { onClick: (reactionCode) => reactToComment({
                                reactionCode,
                                resourceId,
                                commentId,
                            }), likeButtonText: likeButtonText, activeReaction: reactionsState.activeReaction, config: reactionsConfig, isMobile: isMobile, t: t, isDisabled: !canReact }),
                        replyButton))));
        }
        case 'emotions': {
            return (React.createElement(CommentFooterLayout, { aside: React.createElement(EmotionsMetadataLazy, { isPending: reactionsState.pending, reactionsState: reactionsState, commentId: commentId, config: reactionsConfig, t: t, isMobile: isMobile }) },
                React.createElement(Skeleton, { showSkeleton: interactionFetchState === 'not_fetched' },
                    React.createElement("div", { className: classes.contentWrapper },
                        React.createElement(EmotionsButton, { className: classes.emotionsButton, isPending: reactionsState.pending, isMobile: isMobile, isDisabled: !canReact || reactionsState.pending, onClick: (reactionCode) => reactToComment({
                                reactionCode,
                                resourceId,
                                commentId,
                            }), reactions: reactionsState.reactions, config: reactionsConfig, t: t }),
                        replyButton))));
        }
        case 'votes': {
            const disabled = !canVote;
            const action = disabled
                ? { disabled }
                : {
                    cb: (action) => voteOnComment({
                        subaction: action,
                        resourceId,
                        commentId,
                        ctxFields,
                    }),
                };
            return (React.createElement(CommentFooterLayout, { aside: null },
                React.createElement(Skeleton, { showSkeleton: interactionFetchState === 'not_fetched' },
                    React.createElement("div", { className: classes.contentWrapper },
                        React.createElement(Voting, { className: classes.voting, onVote: action.disabled ? noop : action.cb, voteState: votesState, isDisabled: !!action.disabled }),
                        replyButton))));
        }
        default:
            throw unreachable(reactionsConfig);
    }
};
export const ReplyFooter = (props) => {
    /* Votes can not be in reply footer */
    return (React.createElement(CommentFooter, { ...props, canVote: false, votesState: { pending: false, state: 'NEUTRAL', score: 0 } }));
};
const CommentFooterLayout = ({ children, aside }) => {
    const isMobile = useApi(getIsMobile);
    return (React.createElement("div", { className: st(classes.root, { isMobile }, classesReactions.params) },
        React.createElement("div", { className: classes.leftSide }, children),
        aside));
};
const ReplyButton = (props) => {
    const t = useTranslate();
    const replyButtonText = !props.disabled ? props.replyButtonText : undefined;
    return (React.createElement("button", { "data-hook": REPLY_BTN, className: st(classes.action, { defaultCursor: !!props.disabled }, classes.withHoverEffect), onClick: props.disabled ? undefined : props.onReply, disabled: props.disabled },
        React.createElement(ReplyIcon, { className: classes.actionIcon }),
        React.createElement("span", { "data-hook": REPLY_TEXT, className: classes.actionTitle }, replyButtonText ?? t('comment.reply'))));
};
export default CommentFooterLayout;
//# sourceMappingURL=comment-footer.js.map